import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { ProjectContactFormSchema } from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { client } from "@/hono";
import { AddContactMemberModal } from "@/modals/contacts/add-contact-member-modal";
import { ProjectContactMemberSelectCard } from "./project-contact-member-select-card";

/** Props Interface */
interface ProjectContactMembersTableProps {
	contactId: Nullable<string>;
}

export function ProjectContactMembers({ contactId }: ProjectContactMembersTableProps) {
	/** Context */
	const methods = useFormContext<ProjectContactFormSchema>();
	const currentContactMemberIds = useWatch({ control: methods.control, name: "contactMemberIds" });

	/** Hooks */
	const { t } = useTranslation();
	const addContactMemberModal = useDisclosure();

	/** Query */
	const { data: contactMembers, refetch } = useQuery({
		queryKey: ["contact-members", contactId || ""],
		queryFn: async () => {
			if (!contactId) return [];
			const response = await client.contacts[":contactId"].members.$get({
				param: { contactId },
			});
			const data = await responseHandler(response);
			return data.members;
		},
		enabled: !!contactId,
	});

	if (!contactId) return null;

	/** Functions */
	function handleOnToggleContactMember(contactMemberId: string) {
		const currentMembers = methods.getValues("contactMemberIds") || [];
		if (currentMembers.includes(contactMemberId)) {
			methods.setValue(
				"contactMemberIds",
				currentMembers.filter((id) => id !== contactMemberId),
			);
		} else {
			methods.setValue("contactMemberIds", [...currentMembers, contactMemberId]);
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="mt-4 v-stack">
				<p className="label">{t("contacts.members")}</p>
				{!contactMembers?.length ? (
					<BlankSlate
						onClick={addContactMemberModal.onOpen}
						data-cy="project-contact-member-add-button"
					/>
				) : (
					<React.Fragment>
						{contactMembers?.map((contactMember) => (
							<ProjectContactMemberSelectCard
								key={contactMember.id}
								contactMember={contactMember}
								onClick={handleOnToggleContactMember}
								isIncluded={currentContactMemberIds.includes(contactMember.id)}
							/>
						))}
						<div className="self-end mt-2">
							<Button
								onClick={addContactMemberModal.onOpen}
								icon={<Icon name="Plus" />}
								data-cy="project-contact-member-add-button"
							>
								{t("contacts.add_member")}
							</Button>
						</div>
					</React.Fragment>
				)}
			</div>
			<AddContactMemberModal
				isOpen={addContactMemberModal.isOpen}
				onClose={addContactMemberModal.onClose}
				contactId={contactId}
				onComplete={async (_contactMember) => {
					if (!_contactMember) return;
					handleOnToggleContactMember(_contactMember.id);
					await refetch();
				}}
			/>
		</React.Fragment>
	);
}
